import { Component } from 'react';
import { csvCell } from './advisorCsv';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { BsFillPersonFill } from 'react-icons/bs';
import {
  FaBuilding,
  FaChartPie,
  FaChild,
  FaCoins,
  FaCubes,
  FaPeopleArrows,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiArrowRight,
  FiPlus,
  FiSearch,
} from 'react-icons/fi';
import {
  breakpointMedium,
  breakpointSmall,
  breakpointXSmall,
} from 'src/design/styleguide/common/breakpoints';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import { Heading } from 'src/design/styleguide/Headings';
import Text from 'src/design/styleguide/text/Text';
import Loading from 'src/components/loading/Loading';
import WidgetPanel from 'src/components/dashboard/common/WidgetPanel';
import AdvisorTableEmptyState from './AdvisorTableEmptyState';
import ClientOwnershipPanel from './ClientOwnershipPanel';
import {
  EmptyStateContent,
  NoActiveClientsIllustration,
} from 'src/components/dashboard/pendingOrders/NoOrders';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import SecondaryButton from 'src/design/components/button/SecondaryButton';
import {
  BodyTableRow,
  ColumnHeading,
  StickyHeader,
  Table,
  TableData,
} from 'src/design/components/table/Table';
import Popover from 'scripts/components/shared/Popover';
import RegistrationStep from 'scripts/constants/RegistrationStep';
import AdvisorService from 'scripts/services/AdvisorService';
import { errorAlert } from 'scripts/redux/actions/alerts';
import Formatters from 'scripts/utilities/formattersV2';
import { ACCOUNTTYPES } from 'src/settings/registration';
import { greenDark, greenLight, purple50 } from 'src/themes/brickx/colors';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import history from 'src/browser/history';
import {
  advisorClientDetailUrl,
  advisorClientOnboardingUrl,
} from 'src/utils/pageUrls';

const PAGE_SIZE = 10;
const UNAVAILABLE_FINANCIAL_TITLE =
  'This value is not returned by the clients overview endpoint.';

const ONBOARDING_STATUS = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in-progress',
  PENDING: 'pending',
};

const ONBOARDING_LABELS = {
  [ONBOARDING_STATUS.COMPLETED]: 'Completed',
  [ONBOARDING_STATUS.IN_PROGRESS]: 'In Progress',
  [ONBOARDING_STATUS.PENDING]: 'Pending',
};

const ACCOUNT_TYPE_LABELS = {
  [ACCOUNTTYPES.INDIVIDUAL]: 'Individual',
  [ACCOUNTTYPES.MINOR]: 'Minor',
  [ACCOUNTTYPES.SMSF]: 'SMSF',
  [ACCOUNTTYPES.TRUST]: 'Trust',
  [ACCOUNTTYPES.CORPORATION]: 'Company',
};

const ACCOUNT_TYPE_ICONS = {
  [ACCOUNTTYPES.INDIVIDUAL]: BsFillPersonFill,
  [ACCOUNTTYPES.MINOR]: FaChild,
  [ACCOUNTTYPES.SMSF]: FaCoins,
  [ACCOUNTTYPES.TRUST]: FaPeopleArrows,
  [ACCOUNTTYPES.CORPORATION]: FaBuilding,
};

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All account types' },
  { value: ACCOUNTTYPES.INDIVIDUAL, label: 'Individual' },
  { value: ACCOUNTTYPES.MINOR, label: 'Minor' },
  { value: ACCOUNTTYPES.SMSF, label: 'SMSF' },
  { value: ACCOUNTTYPES.TRUST, label: 'Trust' },
  { value: ACCOUNTTYPES.CORPORATION, label: 'Company' },
];

const ACCOUNT_TYPE_CHART_ITEMS = [
  { value: ACCOUNTTYPES.INDIVIDUAL, label: 'Individual' },
  { value: ACCOUNTTYPES.SMSF, label: 'SMSF' },
  { value: ACCOUNTTYPES.TRUST, label: 'Trust' },
  { value: ACCOUNTTYPES.CORPORATION, label: 'Company' },
  { value: ACCOUNTTYPES.MINOR, label: 'Minor' },
];

const ONBOARDING_OPTIONS = [
  { value: '', label: 'All onboarding' },
  { value: ONBOARDING_STATUS.COMPLETED, label: 'Completed' },
  { value: ONBOARDING_STATUS.IN_PROGRESS, label: 'In Progress' },
  { value: ONBOARDING_STATUS.PENDING, label: 'Pending' },
];

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatCurrency = (value) =>
  value === null ? '—' : Formatters.numeric.dollarDecimal(value);

const formatSummaryCurrency = (value) =>
  Formatters.numeric.dollarAbbreviated(value).toUpperCase();

const formatCompactCurrency = (value) => {
  if (value === null) return '—';

  const absoluteValue = Math.abs(value);
  if (absoluteValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1).replace('.0', '')}M`;
  }
  if (absoluteValue >= 1000) return `$${Math.round(value / 1000)}K`;

  return `$${Math.round(value)}`;
};

const formatDate = (value) => (value ? Formatters.time.date(value) : '—');

const normalizeAccountType = (value) => {
  const normalized = String(value || '').toLowerCase();
  return normalized === 'company' ? ACCOUNTTYPES.CORPORATION : normalized;
};

export const getOnboardingStatus = (nextRegistrationStep) => {
  const step = String(
    nextRegistrationStep || RegistrationStep.SIGNUP,
  ).toLowerCase();

  if (step === RegistrationStep.DONE) return ONBOARDING_STATUS.COMPLETED;
  if (step === RegistrationStep.SIGNUP || step === RegistrationStep.VERIFY) {
    return ONBOARDING_STATUS.PENDING;
  }

  return ONBOARDING_STATUS.IN_PROGRESS;
};

export const normalizeAdvisorClientOverview = (overview) => {
  const clientProfile = overview.clientProfile || {};
  const relationship = overview.relationship || {};
  const financialSummary = overview.financialSummary || null;
  const account = financialSummary ? financialSummary.account || {} : {};
  const holdings = financialSummary ? financialSummary.holdings || {} : {};
  const accountType = normalizeAccountType(clientProfile.accountType);
  const onboardingStatus = getOnboardingStatus(
    clientProfile.nextRegistrationStep,
  );

  return {
    id: clientProfile.id,
    name: clientProfile.name || clientProfile.email || 'Client',
    email: clientProfile.email || '—',
    accountType,
    accountTypeLabel: ACCOUNT_TYPE_LABELS[accountType] || '-',
    registrationStep: clientProfile.nextRegistrationStep || null,
    onboardingStatus,
    onboardingLabel: ONBOARDING_LABELS[onboardingStatus],
    availableBalance: numberOrNull(account.availableToTradeAmount),
    portfolioValue: numberOrNull(account.totalPortfolioValue),
    accountValue: numberOrNull(account.totalAccountValue),
    bricksOwned: numberOrNull(holdings.totalBricksOwned),
    propertiesOwnership: holdings.propertiesOwnership || {},
    clientSince: relationship.updatedAt,
    financialLastUpdatedAt: financialSummary && financialSummary.lastUpdatedAt,
  };
};

export const normalizeAdvisorClientRelationship = (relationship) => {
  const accountType = normalizeAccountType(relationship.accountType);

  return {
    id: relationship.clientId,
    name: relationship.name || relationship.email || 'Client',
    email: relationship.email || '—',
    accountType,
    accountTypeLabel: ACCOUNT_TYPE_LABELS[accountType] || '-',
    registrationStep: null,
    onboardingStatus: null,
    onboardingLabel: 'Unavailable',
    availableBalance: null,
    portfolioValue: null,
    accountValue: null,
    bricksOwned: null,
    propertiesOwnership: {},
    clientSince: relationship.createdAt,
    financialLastUpdatedAt: null,
  };
};

const getInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const sortClientsByNewest = (clients) =>
  clients.sort((firstClient, secondClient) => {
    const firstClientSince = Date.parse(firstClient.clientSince) || 0;
    const secondClientSince = Date.parse(secondClient.clientSince) || 0;

    return secondClientSince - firstClientSince;
  });

const getClientSummary = (clients) =>
  clients.reduce(
    (summary, client) => ({
      totalClients: summary.totalClients + 1,
      portfolioValue:
        summary.portfolioValue + Number(client.portfolioValue || 0),
      availableBalance:
        summary.availableBalance + Number(client.availableBalance || 0),
      bricksOwned: summary.bricksOwned + Number(client.bricksOwned || 0),
      hasPortfolioValue:
        summary.hasPortfolioValue || client.portfolioValue !== null,
      hasAvailableBalance:
        summary.hasAvailableBalance || client.availableBalance !== null,
      hasBricksOwned: summary.hasBricksOwned || client.bricksOwned !== null,
    }),
    {
      totalClients: 0,
      portfolioValue: 0,
      availableBalance: 0,
      bricksOwned: 0,
      hasPortfolioValue: false,
      hasAvailableBalance: false,
      hasBricksOwned: false,
    },
  );

export const getOnboardingBreakdown = (clients) =>
  clients.reduce(
    (breakdown, client) =>
      client.onboardingStatus
        ? {
            ...breakdown,
            [client.onboardingStatus]: breakdown[client.onboardingStatus] + 1,
          }
        : breakdown,
    {
      [ONBOARDING_STATUS.COMPLETED]: 0,
      [ONBOARDING_STATUS.IN_PROGRESS]: 0,
      [ONBOARDING_STATUS.PENDING]: 0,
    },
  );

export const getOnboardingTotal = (breakdown) =>
  Object.values(ONBOARDING_STATUS).reduce(
    (total, status) => total + breakdown[status],
    0,
  );

export const getPortfolioByAccountType = (clients) =>
  ACCOUNT_TYPE_CHART_ITEMS.map(({ value, label }) => ({
    value,
    label,
    portfolioValue: clients
      .filter((client) => client.accountType === value)
      .reduce((total, client) => total + Number(client.portfolioValue || 0), 0),
  }));

export const getClientOwnershipByInvestment = (clients) => {
  const ownership = new Map();
  const seenClients = new Set();

  clients.forEach((client) => {
    const clientId = client.clientProfile && client.clientProfile.id;
    if (clientId) {
      if (seenClients.has(clientId)) return;
      seenClients.add(clientId);
    }
    const holdings =
      client.financialSummary && client.financialSummary.holdings;
    const propertiesOwnership = holdings && holdings.propertiesOwnership;

    Object.entries(propertiesOwnership || {}).forEach(
      ([investmentCode, value]) => {
        const bricks = numberOrNull(value);
        if (bricks === null || bricks <= 0) return;
        ownership.set(
          investmentCode,
          (ownership.get(investmentCode) || 0) + bricks,
        );
      },
    );
  });

  return Array.from(ownership, ([investmentCode, totalBricks]) => ({
    investmentCode,
    totalBricks,
  })).sort(
    (first, second) =>
      second.totalBricks - first.totalBricks ||
      first.investmentCode.localeCompare(second.investmentCode),
  );
};

const getNicePortfolioIncrement = (minimumIncrement) => {
  const magnitude = 10 ** Math.floor(Math.log10(minimumIncrement));
  const normalizedIncrement = minimumIncrement / magnitude;
  const normalizedSteps = [1, 1.5, 2, 2.5, 3, 5, 10];
  const normalizedStep =
    normalizedSteps.find((step) => step >= normalizedIncrement) || 10;

  return normalizedStep * magnitude;
};

export const getPortfolioChartScale = (maximumValue) => {
  if (!maximumValue) {
    return { intervals: 4, maximum: 1, values: [1, 0.75, 0.5, 0.25, 0] };
  }

  const intervals = 4;
  const increment = getNicePortfolioIncrement(maximumValue / intervals);
  const maximum = increment * intervals;

  return {
    intervals,
    maximum,
    values: Array.from(
      { length: intervals + 1 },
      (unused, index) => maximum - increment * index,
    ),
  };
};

const getPercentage = (count, total) =>
  total ? Math.round((count / total) * 100) : 0;

export const getDonutGradient = (breakdown, total) => {
  if (!total) return 'conic-gradient(var(--bx-color---grey-200) 0 100%)';

  const completedEnd = (breakdown[ONBOARDING_STATUS.COMPLETED] / total) * 100;
  const inProgressEnd =
    completedEnd + (breakdown[ONBOARDING_STATUS.IN_PROGRESS] / total) * 100;

  return `conic-gradient(
    var(--bx-color---purple-600) 0 ${completedEnd}%,
    var(--bx-color---blue-500) ${completedEnd}% ${inProgressEnd}%,
    var(--bx-color---yellow-500) ${inProgressEnd}% 100%
  )`;
};

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (unused, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) items.push('start-ellipsis');
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < totalPages - 1) items.push('end-ellipsis');
  items.push(totalPages);

  return items;
};

export const buildClientsCsv = (clients) => {
  const rows = [
    [
      'Client ID',
      'Client',
      'Email',
      'Account Type',
      'Registration Step',
      'Onboarding',
      'Portfolio Value',
      'Account Value',
      'Available Balance',
      'Bricks Owned',
      'Relationship Updated At',
      'Financial Summary Last Updated At',
    ],
    ...clients.map((client) => [
      client.id,
      client.name,
      client.email,
      client.accountTypeLabel,
      client.registrationStep,
      client.onboardingLabel,
      client.portfolioValue,
      client.accountValue,
      client.availableBalance,
      client.bricksOwned,
      client.clientSince,
      client.financialLastUpdatedAt,
    ]),
  ];

  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};

const PagePanels = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  gap: 16px;
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: ${gridUnit * 4}px;

  ${breakpointSmall(`
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    column-gap: ${gridUnit * 4}px;
    row-gap: ${gridUnit * 4}px;
  `)}
`;

const HeaderCopy = styled.div`
  min-width: 0;

  ${breakpointSmall(`
    flex: 1 1 280px;
  `)}
`;

const PageTitle = styled(Heading).attrs({
  as: 'h1',
  align: 'left',
  fontSize: 'small',
})`
  margin: 0;
`;

const PageSubtitle = styled(Text).attrs({
  as: 'p',
  fontSize: '3x-small',
})`
  margin: ${gridUnit * 2}px 0 0;
  color: var(--bx-color---grey-700);
  line-height: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: ${gridUnit * 2}px;

  ${breakpointSmall(`
    transform: translateY(-2px);
  `)}
`;

const ExportButton = styled(SecondaryButton)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${gridUnit * 2}px;
    min-width: 120px;
    height: 45px;
    padding: 0 ${gridUnit * 3}px;
    border-radius: var(--bx-radius);
    font-size: 1.8rem;
  }
`;

const AddClientButton = styled(PrimaryButton).attrs({
  color: PrimaryButton.colors.PURPLE,
  size: PrimaryButton.sizes.EXTRA_SMALL,
})`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${gridUnit * 2}px;
    min-width: 140px;
    height: 45px;
    padding: 0 ${gridUnit * 3}px;
    border-radius: var(--bx-radius);
    background-color: ${themeForAdvisorAccount.colors.primary};
    font-size: 1.8rem;
  }
`;

const SummaryGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;

  ${breakpointXSmall(`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `)}

  @media (min-width: 1250px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const SummaryCardContainer = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  min-width: 0;

  & > div {
    display: flex;
    box-sizing: border-box;
    flex-direction: column;
    padding: ${gridUnit * 4}px;

    ${breakpointSmall(`
      padding: ${gridUnit * 5}px ${gridUnit * 6}px;
    `)}
  }

  @media (max-width: 575px) {
    & > div {
      align-items: center;
    }
  }
`;

const SummaryLabel = styled(Text).attrs({
  as: 'div',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  color: var(--bx-color---grey-800);

  @media (max-width: 575px) {
    text-align: center;
  }
`;

const SummaryValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 3}px;
  margin-top: ${gridUnit * 2}px;

  @media (max-width: 575px) {
    justify-content: center;
  }
`;

const SummaryIcon = styled.span`
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: ${themeForAdvisorAccount.colors.primary};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const SummaryValue = styled(Text).attrs({
  as: 'div',
  fontSize: 'normal',
  fontWeight: 'bold',
})`
  min-width: 0;
  color: var(--bx-color---grey-900);
  line-height: 1.15;
`;

const SummaryCard = ({ Icon, title, help, value, valueTitle }) => (
  <SummaryCardContainer>
    <div>
      <SummaryLabel>
        {title} <Popover placement="top" content={help} />
      </SummaryLabel>
      <SummaryValueRow>
        <SummaryIcon>
          <Icon aria-hidden="true" />
        </SummaryIcon>
        <SummaryValue title={valueTitle}>{value}</SummaryValue>
      </SummaryValueRow>
    </div>
  </SummaryCardContainer>
);

SummaryCard.propTypes = {
  Icon: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  valueTitle: PropTypes.string,
};

const InsightGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${gridUnit * 4}px;

  &[data-layout='split'] {
    grid-template-columns: minmax(390px, 11fr) minmax(390px, 9fr);
  }

  &[data-layout='hidden'] {
    height: 0;
    min-height: 0;
    gap: 0;
    overflow: hidden;
    visibility: hidden;
  }
`;

const InsightCard = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  height: 100%;
  min-width: 0;
  min-height: 340px;
  overflow: hidden;

  & > div {
    display: flex;
    height: 100%;
    box-sizing: border-box;
    flex-direction: column;
    padding: 0;
  }
`;

const InsightHeader = styled.div`
  padding: ${gridUnit * 6}px ${gridUnit * 4}px ${gridUnit * 4}px;

  ${breakpointSmall(`
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;
  `)}
`;

const InsightTitle = styled(Text).attrs({
  as: 'h2',
  fontSize: '2x-small',
  fontWeight: 'medium',
})`
  margin: 0;
`;

const ChartTitle = styled(InsightTitle)`
  display: flex;
  align-items: center;
  gap: ${gridUnit}px;
`;

const PortfolioChart = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: ${gridUnit * 3}px;
  height: 280px;
  box-sizing: border-box;
  padding: ${gridUnit * 5}px ${gridUnit * 4}px ${gridUnit * 8}px;

  ${breakpointSmall(`
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;
  `)}
`;

const ChartScale = styled.div`
  display: flex;
  height: 200px;
  flex-direction: column;
  justify-content: space-between;
`;

const ChartScaleLabel = styled(Text).attrs({
  as: 'span',
  fontSize: '5x-small',
})`
  display: inline-block;
  align-self: flex-end;
  background: var(--bx-color---surface);
  color: var(--bx-color---grey-800);
  line-height: 1;
  text-align: right;
`;

const ChartPlot = styled.div`
  position: relative;
  display: flex;
  height: 200px;
  align-items: flex-end;
  justify-content: space-around;
  gap: ${gridUnit * 2}px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--bx-color---grey-200);
`;

const ChartGridLine = styled.span`
  position: absolute;
  top: ${(props) => props.$position}%;
  right: 0;
  left: 0;
  border-top: 1px dashed var(--bx-color---grey-200);
`;

const BarGroup = styled.div`
  position: relative;
  display: flex;
  width: 18%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
`;

const BarValue = styled(Text).attrs({
  as: 'span',
  fontSize: '5x-small',
  fontWeight: 'medium',
})`
  margin-bottom: ${gridUnit}px;
  color: var(--bx-color---grey-800);
  font-size: 1.2rem;
  white-space: nowrap;
`;

const ChartBar = styled.div`
  width: clamp(18px, 38%, 34px);
  max-height: calc(100% - 34px);
  min-height: ${(props) => (props.$hasValue ? '3px' : '0')};
  border-radius: 3px 3px 0 0;
  background: ${themeForAdvisorAccount.colors.primary};
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
`;

const BarLabel = styled(Text).attrs({ as: 'span', fontSize: '5x-small' })`
  position: absolute;
  left: 50%;
  bottom: -30px;
  transform: translateX(-50%);
  color: var(--bx-color---grey-800);
  white-space: nowrap;
`;

const OnboardingBody = styled.div`
  display: grid;
  flex: 1;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: ${gridUnit * 5}px;
  align-items: center;
  min-height: 0;
  box-sizing: border-box;
  padding: 0 ${gridUnit * 4}px ${gridUnit * 8}px;

  ${breakpointSmall(`
    grid-template-columns: 180px minmax(0, 1fr);
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;
  `)}

  ${breakpointMedium(`
    grid-template-columns: 180px minmax(0, 1fr);
    gap: ${gridUnit * 4}px;
  `)}

  ${InsightGrid}[data-layout='stacked'] & {
    grid-template-columns: 180px max-content;
    justify-content: center;
  }

  &[data-donut-size='large'] {
    grid-template-columns: 200px minmax(0, 1fr);

    ${InsightGrid}[data-layout='stacked'] & {
      grid-template-columns: 200px max-content;
    }
  }
`;

const OnboardingDonut = styled.div`
  display: flex;
  justify-self: center;
  width: 180px;
  height: 180px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  ${OnboardingBody}[data-donut-size='large'] & {
    width: 200px;
    height: 200px;
  }
`;

const DonutCenter = styled.div`
  display: flex;
  width: 100px;
  height: 100px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bx-color---surface);

  ${OnboardingBody}[data-donut-size='large'] & {
    width: 110px;
    height: 110px;
  }
`;

const DonutTotal = styled(Text).attrs({
  as: 'span',
  fontSize: 'small',
  fontWeight: 'medium',
})`
  color: var(--bx-color---grey-900);
  line-height: 1;
`;

const DonutLabel = styled(Text).attrs({
  as: 'span',
  fontSize: '4x-small',
})`
  margin-top: 2px;
  color: var(--bx-color---grey-800);
`;

const OnboardingLegend = styled.div`
  display: grid;
  gap: ${gridUnit * 8}px;
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto;
  gap: ${gridUnit * 2}px;
  align-items: center;
`;

const LegendDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;

  &.completed {
    background: var(--bx-color---purple-600);
  }

  &.in-progress {
    background: var(--bx-color---blue-500);
  }

  &.pending {
    background: var(--bx-color---yellow-500);
  }
`;

const LegendLabel = styled(Text).attrs({
  as: 'span',
  fontSize: '4x-small',
})`
  color: var(--bx-color---grey-700);
`;

const LegendValue = styled(Text).attrs({
  as: 'span',
  fontSize: '4x-small',
  fontWeight: 'medium',
})`
  color: var(--bx-color---grey-800);
  white-space: nowrap;
`;

const TableCard = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  min-width: 0;
  overflow: hidden;

  & > div {
    padding: 0;
  }
`;

const HelperCaption = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  margin: ${gridUnit * 2}px 0 0;
  color: var(--bx-color---grey-700);
`;

const TableScroll = styled.div`
  width: calc(100% - ${gridUnit * 8}px);
  max-width: calc(100% - ${gridUnit * 8}px);
  margin-right: ${gridUnit * 4}px;
  margin-left: ${gridUnit * 4}px;

  ${breakpointSmall(`
    width: calc(100% - ${gridUnit * 12}px);
    max-width: calc(100% - ${gridUnit * 12}px);
    margin-right: ${gridUnit * 6}px;
    margin-left: ${gridUnit * 6}px;
  `)}
`;

const TableCardHeader = styled.div`
  box-sizing: border-box;
  padding: ${gridUnit * 6}px ${gridUnit * 4}px ${gridUnit * 6}px;

  ${breakpointSmall(`
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;
  `)}
`;

const TableTitle = styled(Text).attrs({
  as: 'h2',
  fontSize: '2x-small',
  fontWeight: 'medium',
})`
  margin: 0;
  color: var(--bx-color---grey-800);
`;

const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gridUnit * 2}px;
  margin-top: ${gridUnit * 6}px;

  &[data-layout='split'] {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > :first-child {
      grid-column: 1 / -1;
    }
  }

  &[data-layout='wide'] {
    display: grid;
    grid-template-columns: minmax(0, 1fr) repeat(2, minmax(0, 176px));
    align-items: center;
    max-width: 600px;

    & > :first-child {
      grid-column: auto;
    }

    & > :not(:first-child) {
      justify-self: end;
      width: 100%;
      max-width: 176px;
    }
  }
`;

const SearchField = styled.label`
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;

  svg {
    position: absolute;
    left: ${gridUnit * 3}px;
    color: var(--bx-color---grey-800);
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 ${gridUnit * 3}px 0 ${gridUnit * 9}px;
    border: 1px solid var(--bx-color---grey-300);
    border-radius: var(--bx-radius);
    background: var(--bx-color---surface);
    color: var(--bx-color---grey-800);
    font-family: inherit;
    font-size: 1.4rem;
  }

  input:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
  }
`;

const SelectField = styled.label`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;

  select {
    width: 100%;
    min-width: 0;
    height: 40px;
    box-sizing: border-box;
    appearance: none;
    padding: 0 ${gridUnit * 8}px 0 ${gridUnit * 3}px;
    border: 1px solid var(--bx-color---grey-300);
    border-radius: var(--bx-radius);
    background: var(--bx-color---surface);
    color: var(--bx-color---grey-700);
    font-family: inherit;
    font-size: 1.4rem;
  }

  select:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
  }

  svg {
    position: absolute;
    right: ${gridUnit * 3}px;
    color: var(--bx-color---grey-700);
    pointer-events: none;
  }
`;

const ClientsTable = styled(Table)`
  width: 100%;
  max-width: 100%;
  min-width: 0;

  & > div {
    max-width: 100%;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: var(--bx-color---grey-50);
    }

    &::-webkit-scrollbar-thumb {
      border: 2px solid var(--bx-color---grey-50);
      border-radius: 8px;
      background: var(--bx-color---grey-300);
    }
  }

  table {
    min-width: 860px;
    border-collapse: collapse;
    table-layout: auto;
  }

  ${breakpointMedium(`
    table {
      min-width: 0;
    }

  `)}
`;

const ClientsColumnHeading = styled(ColumnHeading)`
  padding: ${gridUnit * 3}px;
  color: var(--bx-color---grey-800);
  font-weight: normal;
  text-align: left;
  vertical-align: middle;

  &.client-column {
    padding-left: 0;
  }

  &.action-column {
    width: 1%;
    padding-right: ${gridUnit * 2}px;
    padding-left: 0;
    white-space: nowrap;
  }
`;

const ClientsTableData = styled(TableData)`
  padding: ${gridUnit * 2.5}px ${gridUnit * 3}px;
  border-bottom: 1px solid var(--bx-color---grey-100);
  color: var(--bx-color---grey-800);
  vertical-align: middle;

  &.client-column {
    padding-left: 0;
  }

  &.action-column {
    width: 1%;
    padding-right: ${gridUnit * 2}px;
    padding-left: 0;
    white-space: nowrap;
  }
`;

const ClickableBodyRow = styled(BodyTableRow)`
  height: 58px;
  cursor: pointer;

  &:first-child ${ClientsTableData} {
    padding-top: ${gridUnit * 2.5}px;
  }

  &:hover ${ClientsTableData}, &:focus ${ClientsTableData} {
    background: var(--bx-color---grey-50);
  }

  &:last-child ${ClientsTableData} {
    padding-bottom: ${gridUnit * 2.5}px;
    border-bottom: 0;
  }
`;

const ClientIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 3}px;
`;

const InitialsAvatar = styled.span`
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${purple50};
  color: ${themeForAdvisorAccount.colors.primary};
  font-size: 1.2rem;
  font-weight: bold;
`;

const ClientName = styled.span`
  display: block;
  max-width: 112px;
  min-width: 0;
  overflow: hidden;
  color: var(--bx-color---grey-800);
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Email = styled.span`
  display: block;
  color: var(--bx-color---grey-700);
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AccountType = styled.div`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 2}px;

  & > svg {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    color: ${themeForAdvisorAccount.colors.primary};
  }
`;

const OnboardingPill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 3px ${gridUnit * 2}px;
  border-radius: 3px;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;

  &.completed {
    background: ${greenLight};
    color: ${greenDark};
  }

  &.in-progress {
    background: var(--bx-color---blue-50);
    color: var(--bx-color---blue-600);
  }

  &.pending {
    background: var(--bx-color---yellow-50);
    color: var(--bx-color---yellow-800);
  }

  &.unavailable {
    background: var(--bx-color---grey-100);
    color: var(--bx-color---grey-700);
  }
`;

const AccountValue = styled.span`
  color: var(--bx-color---grey-800);
`;

const RowAction = styled(SecondaryButton)`
  && {
    color: var(--bx-color---grey-800);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    min-width: 36px;
    height: 36px;
    padding: 0;
    border: 2px solid var(--bx-color---grey-500);
  }
`;

const EmptyClientsState = styled.div`
  margin: 32px 0;
`;

const AdvisorClientsTable = ({
  clients,
  isEmptyClientBook,
  onClientSelect,
}) => {
  if (!clients.length) {
    if (!isEmptyClientBook) {
      return (
        <AdvisorTableEmptyState>
          No clients match the selected filters.
        </AdvisorTableEmptyState>
      );
    }

    return (
      <EmptyClientsState>
        <EmptyStateContent
          message="Add a client to begin managing their BrickX account."
          illustration={<NoActiveClientsIllustration />}
          messageTop="x-large"
        />
      </EmptyClientsState>
    );
  }

  return (
    <ClientsTable centeredRows withStickyHeader>
      <thead>
        <tr>
          <StickyHeader>
            <ClientsColumnHeading className="client-column">
              Client
            </ClientsColumnHeading>
          </StickyHeader>
          <ClientsColumnHeading className="email-column">
            Email
          </ClientsColumnHeading>
          <ClientsColumnHeading className="account-type-column">
            Account Type
          </ClientsColumnHeading>
          <ClientsColumnHeading className="onboarding-column">
            Onboarding
          </ClientsColumnHeading>
          <ClientsColumnHeading className="date-column">
            Client Since
          </ClientsColumnHeading>
          <ClientsColumnHeading className="value-column">
            Portfolio
          </ClientsColumnHeading>
          <ClientsColumnHeading
            className="action-column"
            aria-label="Actions"
          />
        </tr>
      </thead>
      <tbody>
        {clients.map((client) => {
          const AccountTypeIcon = ACCOUNT_TYPE_ICONS[client.accountType];

          return (
            <ClickableBodyRow
              key={client.id}
              role="link"
              tabIndex={0}
              onClick={() => onClientSelect(client.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onClientSelect(client.id);
              }}
            >
              <StickyHeader>
                <ClientsTableData className="client-column">
                  <ClientIdentity>
                    <InitialsAvatar>{getInitials(client.name)}</InitialsAvatar>
                    <ClientName title={client.name}>{client.name}</ClientName>
                  </ClientIdentity>
                </ClientsTableData>
              </StickyHeader>
              <ClientsTableData className="email-column">
                <Email title={client.email}>{client.email}</Email>
              </ClientsTableData>
              <ClientsTableData className="account-type-column">
                {AccountTypeIcon ? (
                  <AccountType>
                    <AccountTypeIcon aria-hidden="true" />
                    {client.accountTypeLabel}
                  </AccountType>
                ) : (
                  '-'
                )}
              </ClientsTableData>
              <ClientsTableData className="onboarding-column">
                <OnboardingPill
                  className={client.onboardingStatus || 'unavailable'}
                >
                  {client.onboardingLabel}
                </OnboardingPill>
              </ClientsTableData>
              <ClientsTableData className="date-column">
                {formatDate(client.clientSince)}
              </ClientsTableData>
              <ClientsTableData className="value-column">
                <AccountValue title={formatCurrency(client.portfolioValue)}>
                  {formatCompactCurrency(client.portfolioValue)}
                </AccountValue>
              </ClientsTableData>
              <ClientsTableData className="action-column">
                <RowAction
                  size={SecondaryButton.sizes.EXTRA_SMALL}
                  color={SecondaryButton.colors.PRIMARY}
                  aria-label={`View ${client.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClientSelect(client.id);
                  }}
                >
                  <FiArrowRight aria-hidden="true" />
                </RowAction>
              </ClientsTableData>
            </ClickableBodyRow>
          );
        })}
      </tbody>
    </ClientsTable>
  );
};

AdvisorClientsTable.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEmptyClientBook: PropTypes.bool.isRequired,
  onClientSelect: PropTypes.func.isRequired,
};

const PaginationFooter = styled.footer`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${gridUnit * 3}px;
  align-items: center;
  justify-content: space-between;
  padding: ${gridUnit * 6}px ${gridUnit * 4}px;

  &::before {
    position: absolute;
    top: 0;
    right: ${gridUnit * 4}px;
    left: ${gridUnit * 4}px;
    border-top: 1px solid var(--bx-color---grey-100);
    content: '';
  }

  ${breakpointSmall(`
    flex-direction: row;
    padding-right: ${gridUnit * 6}px;
    padding-left: ${gridUnit * 6}px;

    &::before {
      right: ${gridUnit * 6}px;
      left: ${gridUnit * 6}px;
    }
  `)}
`;

const PageInfo = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  margin: 0;
  color: var(--bx-color---grey-800);
`;

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  gap: ${gridUnit * 3}px;
`;

const PaginationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 ${gridUnit}px;
  border: 0;
  border-radius: var(--bx-radius);
  background: transparent;
  color: var(--bx-color---grey-700);
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
  font-weight: bold;

  &:hover:not(:disabled):not([aria-current='page']) {
    background: var(--bx-color---purple-50);
    color: ${themeForAdvisorAccount.colors.primary};
  }

  &[aria-current='page'] {
    background: ${themeForAdvisorAccount.colors.primary};
    color: var(--bx-color---surface);
  }
`;

const PaginationEllipsis = styled.span`
  min-width: 24px;
  color: var(--bx-color---grey-700);
  font-weight: bold;
  text-align: center;
`;

@connect(null, {
  errorAlert,
})
export default class AdvisorClients extends Component {
  static propTypes = {
    errorAlert: PropTypes.func.isRequired,
  };

  state = {
    loading: true,
    clients: [],
    clientOwnershipByInvestment: null,
    search: '',
    accountTypeFilter: '',
    onboardingFilter: '',
    page: 1,
    filterLayout: 'stacked',
    insightLayout: 'stacked',
    donutSize: 'compact',
    usingLegacyClientList: false,
  };

  unmounted = false;

  filterHeader = null;

  filterHeaderObserver = null;

  insightGrid = null;

  insightGridObserver = null;

  onboardingBodyObserver = null;

  componentDidMount() {
    this.unmounted = false;
    window.addEventListener('resize', this.updateFilterLayout);
    this.loadClients();
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('resize', this.updateFilterLayout);
    if (this.filterHeaderObserver) this.filterHeaderObserver.disconnect();
    if (this.insightGridObserver) this.insightGridObserver.disconnect();
    if (this.onboardingBodyObserver) this.onboardingBodyObserver.disconnect();
  }

  setFilterLayout = (width) => {
    const filterLayout =
      width >= 650 ? 'wide' : width >= 480 ? 'split' : 'stacked';

    if (this.state.filterLayout !== filterLayout) {
      this.setState({ filterLayout });
    }
  };

  updateFilterLayout = () => {
    if (this.filterHeader) {
      this.setFilterLayout(this.filterHeader.getBoundingClientRect().width);
    }
  };

  setFilterHeader = (element) => {
    if (this.filterHeaderObserver) this.filterHeaderObserver.disconnect();
    this.filterHeader = element;

    if (!element) return;

    this.updateFilterLayout();
    if (!window.ResizeObserver) return;

    this.filterHeaderObserver = new window.ResizeObserver((entries) => {
      if (entries.length) this.setFilterLayout(entries[0].contentRect.width);
    });
    this.filterHeaderObserver.observe(element);
  };

  setInsightLayout = (width) => {
    const insightLayout =
      width < 390 ? 'hidden' : width >= 883 ? 'split' : 'stacked';

    if (this.state.insightLayout !== insightLayout) {
      this.setState({ insightLayout });
    }
  };

  setInsightGrid = (element) => {
    if (this.insightGridObserver) this.insightGridObserver.disconnect();
    this.insightGrid = element;

    if (!element) return;

    this.setInsightLayout(element.getBoundingClientRect().width);
    if (!window.ResizeObserver) return;

    this.insightGridObserver = new window.ResizeObserver((entries) => {
      if (entries.length) this.setInsightLayout(entries[0].contentRect.width);
    });
    this.insightGridObserver.observe(element);
  };

  setDonutSize = (width) => {
    const donutSize = width > 400 ? 'large' : 'compact';

    if (this.state.donutSize !== donutSize) {
      this.setState({ donutSize });
    }
  };

  setOnboardingBody = (element) => {
    if (this.onboardingBodyObserver) this.onboardingBodyObserver.disconnect();
    if (!element) return;

    this.setDonutSize(element.getBoundingClientRect().width);
    if (!window.ResizeObserver) return;

    this.onboardingBodyObserver = new window.ResizeObserver((entries) => {
      if (entries.length) this.setDonutSize(entries[0].contentRect.width);
    });
    this.onboardingBodyObserver.observe(element);
  };

  loadClients = async () => {
    try {
      const response = await AdvisorService.getClientsOverview();
      if (!Array.isArray(response)) {
        throw new Error('The advisor clients overview response was invalid.');
      }

      if (!this.unmounted) {
        this.setState({
          loading: false,
          clients: sortClientsByNewest(
            response.map(normalizeAdvisorClientOverview),
          ),
          clientOwnershipByInvestment: getClientOwnershipByInvestment(response),
          usingLegacyClientList: false,
        });
      }
    } catch (error) {
      try {
        const response = await AdvisorService.getClients();
        if (!Array.isArray(response)) {
          throw new Error('The advisor clients response was invalid.');
        }

        if (!this.unmounted) {
          this.setState({
            loading: false,
            clients: sortClientsByNewest(
              response.map(normalizeAdvisorClientRelationship),
            ),
            usingLegacyClientList: true,
          });
        }
      } catch (fallbackError) {
        if (!this.unmounted) {
          this.props.errorAlert(
            'Unable to load adviser clients. Please try again.',
          );
          this.setState({ loading: false });
        }
      }
    }
  };

  getFilteredClients = () => {
    const { clients, search, accountTypeFilter, onboardingFilter } = this.state;
    const normalizedSearch = search.trim().toLowerCase();

    return clients.filter((client) => {
      const matchesSearch =
        !normalizedSearch ||
        client.name.toLowerCase().includes(normalizedSearch) ||
        client.email.toLowerCase().includes(normalizedSearch);
      const matchesAccountType =
        !accountTypeFilter || client.accountType === accountTypeFilter;
      const matchesOnboarding =
        !onboardingFilter || client.onboardingStatus === onboardingFilter;

      return matchesSearch && matchesAccountType && matchesOnboarding;
    });
  };

  selectClient = (clientId) => {
    if (clientId) history.push(advisorClientDetailUrl(clientId));
  };

  exportClients = () => {
    const clients = this.getFilteredClients();
    if (!CLIENT || !clients.length) return;

    const blob = new Blob([buildClientsCsv(clients)], {
      type: 'text/csv;charset=utf-8',
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = window.document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = 'adviser-clients.csv';
    window.document.body.appendChild(anchor);
    anchor.click();
    window.document.body.removeChild(anchor);
    window.URL.revokeObjectURL(downloadUrl);
  };

  renderPagination = (currentPage, totalPages) => (
    <Pagination aria-label="Client pages">
      <PaginationButton
        type="button"
        aria-label="Previous page"
        disabled={currentPage === 1}
        onClick={() => this.setState({ page: currentPage - 1 })}
      >
        <FiChevronLeft aria-hidden="true" />
      </PaginationButton>
      {getPaginationItems(currentPage, totalPages).map((item) =>
        typeof item === 'number' ? (
          <PaginationButton
            key={item}
            type="button"
            aria-label={`Page ${item}`}
            aria-current={item === currentPage ? 'page' : undefined}
            onClick={() => this.setState({ page: item })}
          >
            {item}
          </PaginationButton>
        ) : (
          <PaginationEllipsis key={item}>…</PaginationEllipsis>
        ),
      )}
      <PaginationButton
        type="button"
        aria-label="Next page"
        disabled={currentPage === totalPages}
        onClick={() => this.setState({ page: currentPage + 1 })}
      >
        <FiChevronRight aria-hidden="true" />
      </PaginationButton>
    </Pagination>
  );

  render() {
    const {
      loading,
      clients,
      search,
      accountTypeFilter,
      onboardingFilter,
      page,
      filterLayout,
      insightLayout,
      donutSize,
      usingLegacyClientList,
    } = this.state;

    if (loading) return <Loading />;

    const summary = getClientSummary(clients);
    const isEmptyClientBook = !clients.length;
    const onboardingBreakdown = getOnboardingBreakdown(clients);
    const onboardingTotal = getOnboardingTotal(onboardingBreakdown);
    const filteredClients = this.getFilteredClients();
    const totalPages = Math.max(
      1,
      Math.ceil(filteredClients.length / PAGE_SIZE),
    );
    const currentPage = Math.min(page, totalPages);
    const firstResultIndex = (currentPage - 1) * PAGE_SIZE;
    const visibleClients = filteredClients.slice(
      firstResultIndex,
      firstResultIndex + PAGE_SIZE,
    );
    const firstResult = filteredClients.length ? firstResultIndex + 1 : 0;
    const lastResult = Math.min(
      firstResultIndex + PAGE_SIZE,
      filteredClients.length,
    );
    const onboardingGradient = getDonutGradient(
      onboardingBreakdown,
      onboardingTotal,
    );
    const portfolioByAccountType = getPortfolioByAccountType(clients);
    const portfolioMaximum = Math.max(
      ...portfolioByAccountType.map(({ portfolioValue }) => portfolioValue),
    );
    const chartScale = getPortfolioChartScale(portfolioMaximum);
    return (
      <PagePanels data-test-reference="advisor-clients-page">
        <PageHeader>
          <HeaderCopy>
            <PageTitle>Adviser Clients</PageTitle>
            <PageSubtitle>
              View and manage all your connected BrickX clients in one place.
            </PageSubtitle>
          </HeaderCopy>
          <HeaderActions>
            <ExportButton
              size={SecondaryButton.sizes.EXTRA_SMALL}
              color={SecondaryButton.colors.PRIMARY}
              disabled={!filteredClients.length}
              onClick={this.exportClients}
            >
              <FiDownload aria-hidden="true" />
              Export
            </ExportButton>
            <AddClientButton link={advisorClientOnboardingUrl()}>
              <FiPlus aria-hidden="true" />
              Add Client
            </AddClientButton>
          </HeaderActions>
        </PageHeader>

        <SummaryGrid aria-label="Client book summary">
          <SummaryCard
            Icon={FaUsers}
            title="Total Clients"
            help="Clients currently connected to your adviser account."
            value={Formatters.numeric.number(summary.totalClients)}
          />
          <SummaryCard
            Icon={FaCubes}
            title="Bricks Owned"
            help="Total Bricks currently owned across connected clients."
            value={
              summary.hasBricksOwned
                ? Formatters.numeric.number(summary.bricksOwned)
                : '—'
            }
          />
          <SummaryCard
            Icon={FaChartPie}
            title="Portfolio Value"
            help="Current total value of connected clients' portfolios."
            value={
              summary.hasPortfolioValue
                ? formatSummaryCurrency(summary.portfolioValue)
                : '—'
            }
            valueTitle={
              summary.hasPortfolioValue
                ? Formatters.numeric.dollarDecimal(summary.portfolioValue)
                : UNAVAILABLE_FINANCIAL_TITLE
            }
          />
          <SummaryCard
            Icon={FaWallet}
            title="Available Cash"
            help="Total available cash across connected clients."
            value={
              summary.hasAvailableBalance
                ? formatSummaryCurrency(summary.availableBalance)
                : '—'
            }
            valueTitle={
              summary.hasAvailableBalance
                ? Formatters.numeric.dollarDecimal(summary.availableBalance)
                : UNAVAILABLE_FINANCIAL_TITLE
            }
          />
        </SummaryGrid>

        <InsightGrid ref={this.setInsightGrid} data-layout={insightLayout}>
          <InsightCard>
            <InsightHeader>
              <ChartTitle>
                Portfolio Value
                <Popover
                  placement="top"
                  content="Current portfolio value grouped by client account type."
                />
              </ChartTitle>
            </InsightHeader>
            <PortfolioChart
              role="img"
              aria-label="Current portfolio value by client account type"
            >
              <ChartScale>
                {chartScale.values.map((scale) => (
                  <ChartScaleLabel key={scale}>
                    {formatCompactCurrency(scale)}
                  </ChartScaleLabel>
                ))}
              </ChartScale>
              <ChartPlot>
                {Array.from(
                  { length: chartScale.intervals },
                  (unused, index) => (
                    <ChartGridLine
                      key={index}
                      $position={(index * 100) / chartScale.intervals}
                    />
                  ),
                )}
                {portfolioByAccountType.map((item) => (
                  <BarGroup key={item.value}>
                    <BarValue>
                      {item.portfolioValue
                        ? formatCompactCurrency(item.portfolioValue)
                        : '$0'}
                    </BarValue>
                    <ChartBar
                      $hasValue={item.portfolioValue > 0}
                      style={{
                        height: `${
                          (item.portfolioValue / chartScale.maximum) * 100
                        }%`,
                      }}
                      title={`${item.label}: ${formatCurrency(
                        item.portfolioValue,
                      )}`}
                    />
                    <BarLabel>{item.label}</BarLabel>
                  </BarGroup>
                ))}
              </ChartPlot>
            </PortfolioChart>
          </InsightCard>

          <InsightCard>
            <InsightHeader>
              <ChartTitle>
                Onboarding Status
                <Popover
                  placement="top"
                  content="Pending means the account is created; In Progress means setup is underway; Completed means setup is done and the client has invested."
                />
              </ChartTitle>
            </InsightHeader>
            <OnboardingBody
              ref={this.setOnboardingBody}
              data-donut-size={donutSize}
            >
              <OnboardingDonut
                role="img"
                aria-label={
                  onboardingTotal
                    ? `${onboardingBreakdown.completed} completed, ${onboardingBreakdown['in-progress']} in progress, ${onboardingBreakdown.pending} pending`
                    : 'Onboarding status unavailable'
                }
                style={{ background: onboardingGradient }}
              >
                <DonutCenter>
                  <DonutTotal>{summary.totalClients || '—'}</DonutTotal>
                  {!!summary.totalClients && <DonutLabel>Total</DonutLabel>}
                </DonutCenter>
              </OnboardingDonut>
              <OnboardingLegend>
                {Object.values(ONBOARDING_STATUS).map((status) => {
                  const count = onboardingBreakdown[status];
                  return (
                    <LegendRow key={status}>
                      <LegendDot className={status} />
                      <LegendLabel>{ONBOARDING_LABELS[status]}</LegendLabel>
                      <LegendValue>
                        {onboardingTotal
                          ? `${count} (${getPercentage(
                              count,
                              onboardingTotal,
                            )}%)`
                          : '—'}
                      </LegendValue>
                    </LegendRow>
                  );
                })}
              </OnboardingLegend>
            </OnboardingBody>
          </InsightCard>
        </InsightGrid>

        <TableCard>
          <TableCardHeader ref={this.setFilterHeader}>
            <TableTitle>Active Clients</TableTitle>
            {usingLegacyClientList && (
              <HelperCaption>
                Financial and onboarding details are unavailable.
              </HelperCaption>
            )}
            {!isEmptyClientBook && (
              <FilterBar data-layout={filterLayout}>
                <SearchField>
                  <FiSearch aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    placeholder="Search clients..."
                    aria-label="Search clients by name or email"
                    onChange={(event) =>
                      this.setState({ search: event.target.value, page: 1 })
                    }
                  />
                </SearchField>
                <SelectField>
                  <select
                    value={accountTypeFilter}
                    aria-label="Filter by account type"
                    onChange={(event) =>
                      this.setState({
                        accountTypeFilter: event.target.value,
                        page: 1,
                      })
                    }
                  >
                    {ACCOUNT_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown aria-hidden="true" />
                </SelectField>
                <SelectField>
                  <select
                    value={onboardingFilter}
                    aria-label="Filter by onboarding status"
                    onChange={(event) =>
                      this.setState({
                        onboardingFilter: event.target.value,
                        page: 1,
                      })
                    }
                  >
                    {ONBOARDING_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown aria-hidden="true" />
                </SelectField>
              </FilterBar>
            )}
          </TableCardHeader>

          <TableScroll>
            <AdvisorClientsTable
              clients={visibleClients}
              isEmptyClientBook={isEmptyClientBook}
              onClientSelect={this.selectClient}
            />
          </TableScroll>

          {filteredClients.length > 0 && (
            <PaginationFooter>
              <PageInfo>
                {`Showing ${firstResult} to ${lastResult} of ${filteredClients.length} clients`}
              </PageInfo>
              {this.renderPagination(currentPage, totalPages)}
            </PaginationFooter>
          )}
        </TableCard>
        <ClientOwnershipPanel
          ownership={this.state.clientOwnershipByInvestment}
          clients={clients}
        />
      </PagePanels>
    );
  }
}
